import {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Divider, Select, Space, Text, Title, Flex } from "@mantine/core";
import { Controller, useForm, FieldErrors, Control } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import { UPDATE_PRODUCT } from "../../../gql/product";
import {
  formatChoicesData,
  filterShadeByShadeType,
  getTeethsFromLowerArch,
  getTeethsFromUpperArch,
} from "../../../utils/item.utils";
import { PartialAndFullDentureProsthesisShadeType } from "../../../types/enums";
import {
  FullDentureTeethShape,
  shadeTypeChoices,
} from "../../../types/constants";
import { Product } from "../../../types/interfaces";
import type { GetRemovableItemChoicesQuery } from "../../../gql/generated/graphql";

const DEFAULT_SHADE_ID = "A3_VITA_CLASSIC";

const getDefaultShadeValue = (
  shades: { value: string; label: string }[]
): string => {
  const a3 = shades.find(
    s => s.value === DEFAULT_SHADE_ID || s.label.startsWith(DEFAULT_SHADE_ID)
  );
  return a3?.value ?? shades[0]?.value ?? "";
};

type PartialDentureAestheticsFormData = {
  shadeType: PartialAndFullDentureProsthesisShadeType;
  teethShadeId: string;
  gingivaShadeId: string;
  teethShape: string;
  maxillaireShadeType?: PartialAndFullDentureProsthesisShadeType;
  maxillaireTeethShadeId?: string;
  maxillaireGingivaShadeId?: string;
  maxillaireTeethShape?: string;
  mandibulaireShadeType?: PartialAndFullDentureProsthesisShadeType;
  mandibulaireTeethShadeId?: string;
  mandibulaireGingivaShadeId?: string;
  mandibulaireTeethShape?: string;
};

const ArcadeAesthetics = ({
  control,
  fieldNames,
  filteredTeethShades,
  gingivaShadeChoices,
  errors,
  shadeTypeChoices,
}: {
  control: Control<PartialDentureAestheticsFormData>;
  fieldNames: {
    shadeType: keyof PartialDentureAestheticsFormData;
    teethShadeId: keyof PartialDentureAestheticsFormData;
    gingivaShadeId: keyof PartialDentureAestheticsFormData;
    teethShape: keyof PartialDentureAestheticsFormData;
  };
  filteredTeethShades: any[];
  gingivaShadeChoices: any[];
  errors: FieldErrors<PartialDentureAestheticsFormData>;
  shadeTypeChoices: { value: string; label: string }[];
}) => (
  <>
    <Controller
      name={fieldNames.shadeType}
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <ButtonsSelect
          data={shadeTypeChoices}
          value={field.value as string}
          onChange={value => field.onChange(value)}
          numberOfColumns={2}
        />
      )}
    />
    <Space h="lg" />
    <Title order={4}>Teinte des dents</Title>
    <Space h="xs" />
    <Controller
      name={fieldNames.teethShadeId}
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <Select
          placeholder="Sélectionner une teinte"
          data={filteredTeethShades}
          value={field.value as string}
          onChange={value => field.onChange(value ?? "")}
          searchable
          allowDeselect={false}
        />
      )}
    />
    {errors?.[fieldNames.teethShadeId]?.type === "required" && (
      <Text size="sm" style={{ color: "red" }}>
        Veuillez sélectionner une teinte de dents
      </Text>
    )}
    <Space h="lg" />
    <Title order={4}>Teinte de la gencive</Title>
    <Space h="xs" />
    <Controller
      name={fieldNames.gingivaShadeId}
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <ButtonsSelect
          data={gingivaShadeChoices}
          value={field.value as string}
          onChange={value => field.onChange(value)}
          numberOfColumns={2}
        />
      )}
    />
    {errors?.[fieldNames.gingivaShadeId]?.type === "required" && (
      <Text size="sm" style={{ color: "red" }}>
        Veuillez sélectionner une teinte de gencive
      </Text>
    )}
    <Space h="lg" />
    <Title order={4}>Forme des dents</Title>
    <Space h="xs" />
    <Controller
      name={fieldNames.teethShape}
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <ButtonsSelect
          data={FullDentureTeethShape}
          value={field.value as string}
          onChange={value => field.onChange(value)}
          numberOfColumns={3}
        />
      )}
    />
    {errors?.[fieldNames.teethShape]?.type === "required" && (
      <Text size="sm" style={{ color: "red" }}>
        Veuillez sélectionner une forme de dents
      </Text>
    )}
  </>
);

const updateRemovableItemsShades = (
  items: { id: string }[],
  updateRemovableItem: ReturnType<typeof useMutation>[0],
  teethShadeId?: string,
  gingivaShadeId?: string
) =>
  Promise.all(
    items.map(item =>
      updateRemovableItem({
        variables: {
          where: { id: item.id },
          data: {
            ...(teethShadeId && {
              teethShade: { connect: { id: teethShadeId } },
            }),
            ...(gingivaShadeId && {
              gingivaShade: { connect: { id: gingivaShadeId } },
            }),
          },
        },
      })
    )
  );

type PartialDentureAestheticsProps = {
  product: Product;
  removableItemChoicesData: GetRemovableItemChoicesQuery;
};

export interface PartialDentureAestheticsRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemPartialDentureAesthetics = forwardRef<
  PartialDentureAestheticsRef,
  PartialDentureAestheticsProps
>(({ product, removableItemChoicesData }, ref) => {
  const removableItems = product.removableItem ?? [];
  const firstItem = removableItems[0];
  const itemPreferences = product.itemPreferences;

  const isBimaxillarySeparated = firstItem?.isBimaxillarySeparated ?? false;

  const maxillaireItems = useMemo(
    () => removableItems.filter(item => (item.teeth ?? []).some(t => t <= 28)),
    [removableItems]
  );

  const mandibulaireItems = useMemo(
    () => removableItems.filter(item => (item.teeth ?? []).some(t => t >= 31)),
    [removableItems]
  );
  const maxillaireFirstItem = maxillaireItems[0];
  const mandibulaireFirstItem = mandibulaireItems[0];

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<PartialDentureAestheticsFormData>({
    defaultValues: {
      shadeType:
        (itemPreferences?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
        PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
      teethShadeId: firstItem?.teethShade?.id ?? DEFAULT_SHADE_ID,
      gingivaShadeId: firstItem?.gingivaShade?.id ?? "GINGIVA_STANDARD",
      teethShape: itemPreferences?.teethShape ?? "SQUARE",
      maxillaireShadeType:
        (itemPreferences?.partialDentureArcadeData?.maxillaire
          ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
        (itemPreferences?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
        PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
      maxillaireTeethShadeId:
        maxillaireFirstItem?.teethShade?.id ?? DEFAULT_SHADE_ID,
      maxillaireGingivaShadeId:
        maxillaireFirstItem?.gingivaShade?.id ?? "GINGIVA_STANDARD",
      maxillaireTeethShape:
        itemPreferences?.partialDentureArcadeData?.maxillaire?.teethShape ??
        itemPreferences?.teethShape ??
        "SQUARE",
      mandibulaireShadeType:
        (itemPreferences?.partialDentureArcadeData?.mandibulaire
          ?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
        (itemPreferences?.shadeType as PartialAndFullDentureProsthesisShadeType) ??
        PartialAndFullDentureProsthesisShadeType.VITA_CLASSIC,
      mandibulaireTeethShadeId:
        mandibulaireFirstItem?.teethShade?.id ?? DEFAULT_SHADE_ID,
      mandibulaireGingivaShadeId:
        mandibulaireFirstItem?.gingivaShade?.id ?? "GINGIVA_STANDARD",
      mandibulaireTeethShape:
        itemPreferences?.partialDentureArcadeData?.mandibulaire?.teethShape ??
        itemPreferences?.teethShape ??
        "SQUARE",
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM);

  const [updateProduct] = useMutation(UPDATE_PRODUCT);

  const currentShadeType = watch("shadeType");
  const teethShadeId = watch("teethShadeId");
  const gingivaShadeId = watch("gingivaShadeId");
  const teethShape = watch("teethShape");
  const watchMaxillaireShadeType = watch("maxillaireShadeType");
  const watchMandibulaireShadeType = watch("mandibulaireShadeType");
  const maxillaireTeethShadeId = watch("maxillaireTeethShadeId");
  const maxillaireGingivaShadeId = watch("maxillaireGingivaShadeId");
  const maxillaireTeethShape = watch("maxillaireTeethShape");
  const mandibulaireTeethShadeId = watch("mandibulaireTeethShadeId");
  const mandibulaireGingivaShadeId = watch("mandibulaireGingivaShadeId");
  const mandibulaireTeethShape = watch("mandibulaireTeethShape");

  const [filteredTeethShades, setFilteredTeethShades] = useState<any[]>([]);
  const [filteredMaxillaireTeethShades, setFilteredMaxillaireTeethShades] =
    useState<any[]>([]);
  const [filteredMandibulaireTeethShades, setFilteredMandibulaireTeethShades] =
    useState<any[]>([]);

  useEffect(() => {
    if (removableItemChoicesData?.getItemShadesWhere) {
      const filtered = filterShadeByShadeType(
        removableItemChoicesData.getItemShadesWhere,
        currentShadeType
      );
      const formattedShades = formatChoicesData(filtered);
      setFilteredTeethShades(formattedShades);

      const currentTeethShadeId = watch("teethShadeId");
      if (!formattedShades.some((s: any) => s.value === currentTeethShadeId)) {
        setValue("teethShadeId", getDefaultShadeValue(formattedShades));
      }
    }
  }, [currentShadeType, removableItemChoicesData]);

  useEffect(() => {
    if (
      isBimaxillarySeparated &&
      removableItemChoicesData?.getItemShadesWhere
    ) {
      const maxFiltered = filterShadeByShadeType(
        removableItemChoicesData.getItemShadesWhere,
        watchMaxillaireShadeType
      );
      const formattedMaxShades = formatChoicesData(maxFiltered);
      setFilteredMaxillaireTeethShades(formattedMaxShades);

      if (
        !formattedMaxShades.some((s: any) => s.value === maxillaireTeethShadeId)
      ) {
        setValue(
          "maxillaireTeethShadeId",
          getDefaultShadeValue(formattedMaxShades)
        );
      }

      const mandFiltered = filterShadeByShadeType(
        removableItemChoicesData.getItemShadesWhere,
        watchMandibulaireShadeType
      );
      const formattedMandShades = formatChoicesData(mandFiltered);
      setFilteredMandibulaireTeethShades(formattedMandShades);

      if (
        !formattedMandShades.some(
          (s: any) => s.value === mandibulaireTeethShadeId
        )
      ) {
        setValue(
          "mandibulaireTeethShadeId",
          getDefaultShadeValue(formattedMandShades)
        );
      }
    }
  }, [
    watchMaxillaireShadeType,
    watchMandibulaireShadeType,
    isBimaxillarySeparated,
    removableItemChoicesData,
  ]);

  const availableShadeTypeChoices = useMemo(() => {
    const backendTypes = new Set(
      removableItemChoicesData?.getItemShadesWhere
        ?.map((s: any) => s.shadeType)
        .filter(Boolean) ?? []
    );
    return shadeTypeChoices.filter(choice => backendTypes.has(choice.value));
  }, [removableItemChoicesData]);

  const saveData = async (): Promise<void> => {
    const isValidForm = await trigger();
    if (!isValidForm) {
      throw new Error("Formulaire invalide");
    }

    if (!isBimaxillarySeparated) {
      if (product && teethShape && currentShadeType) {
        await updateProduct({
          variables: {
            where: { id: product.id },
            data: {
              itemPreferences: {
                shadeType: currentShadeType,
                teethShape: teethShape,
              },
            },
          },
        });
      }

      await updateRemovableItemsShades(
        removableItems,
        updateRemovableItem,
        teethShadeId,
        gingivaShadeId
      );
    } else {
      if (product) {
        await updateProduct({
          variables: {
            where: { id: product.id },
            data: {
              itemPreferences: {
                ...product.itemPreferences,
                shadeType: watchMaxillaireShadeType,
                teethShape: maxillaireTeethShape,
                partialDentureArcadeData: {
                  maxillaire: {
                    shadeType: watchMaxillaireShadeType,
                    teethShape: maxillaireTeethShape,
                  },
                  mandibulaire: {
                    shadeType: watchMandibulaireShadeType,
                    teethShape: mandibulaireTeethShape,
                  },
                },
              },
            },
          },
        });
      }
      await updateRemovableItemsShades(
        maxillaireItems,
        updateRemovableItem,
        maxillaireTeethShadeId,
        maxillaireGingivaShadeId
      );
      await updateRemovableItemsShades(
        mandibulaireItems,
        updateRemovableItem,
        mandibulaireTeethShadeId,
        mandibulaireGingivaShadeId
      );
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!removableItems.length) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  const gingivaShadeChoices = formatChoicesData(
    removableItemChoicesData?.getItemShadesWhere?.filter(
      (shade: any) => shade.gingiva === true
    ) ?? []
  );

  return (
    <Box>
      {!isBimaxillarySeparated ? (
        <>
          <Title order={3}>Esthétique</Title>
          <Space h="xs" />
          <ArcadeAesthetics
            control={control}
            fieldNames={{
              shadeType: "shadeType",
              teethShadeId: "teethShadeId",
              gingivaShadeId: "gingivaShadeId",
              teethShape: "teethShape",
            }}
            filteredTeethShades={filteredTeethShades}
            gingivaShadeChoices={gingivaShadeChoices}
            errors={errors}
            shadeTypeChoices={availableShadeTypeChoices}
          />
        </>
      ) : (
        <Flex gap="xl" direction={{ base: "column", sm: "row" }}>
          <Box style={{ flex: 1 }}>
            <Title order={3}>Arcade Maxillaire</Title>
            <Space h="md" />
            <ArcadeAesthetics
              control={control}
              fieldNames={{
                shadeType: "maxillaireShadeType",
                teethShadeId: "maxillaireTeethShadeId",
                gingivaShadeId: "maxillaireGingivaShadeId",
                teethShape: "maxillaireTeethShape",
              }}
              filteredTeethShades={filteredMaxillaireTeethShades}
              gingivaShadeChoices={gingivaShadeChoices}
              errors={errors}
              shadeTypeChoices={availableShadeTypeChoices}
            />
          </Box>
          <Divider orientation="vertical" />
          <Box style={{ flex: 1 }}>
            <Title order={3}>Arcade Mandibulaire</Title>
            <Space h="md" />
            <ArcadeAesthetics
              control={control}
              fieldNames={{
                shadeType: "mandibulaireShadeType",
                teethShadeId: "mandibulaireTeethShadeId",
                gingivaShadeId: "mandibulaireGingivaShadeId",
                teethShape: "mandibulaireTeethShape",
              }}
              filteredTeethShades={filteredMandibulaireTeethShades}
              gingivaShadeChoices={gingivaShadeChoices}
              errors={errors}
              shadeTypeChoices={availableShadeTypeChoices}
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
});

CreateRemovableItemPartialDentureAesthetics.displayName =
  "CreateRemovableItemPartialDentureAesthetics";
